import {
  FACT_TYPES,
  KnowledgeKind,
  type Fact,
  type FactType,
  type KnowledgeEntry,
  type KnowledgeSearchResponse,
  type ProvenanceEntry,
} from '@ni/shared';
import type { KnowledgeSearchParams } from '../api/client';

/**
 * **The knowledge base's arithmetic**, kept out of the screen (design note
 * 2026-09-07 §1).
 *
 * The page is one search box, three chips and a panel, and every decision it
 * makes — what reaches the wire, which row the arrow keys move to, how a fact
 * list is grouped, where a provenance line points — is a function of its
 * arguments and is asserted here rather than driven by hand. The SPA suite has
 * no DOM (the ruling `relationship.spec.tsx` records), so anything that is only
 * true inside a React tree is not true anywhere a test can see it.
 */

/** The three kinds, in the order the chips and the opening lists read. */
export const KNOWLEDGE_KINDS: readonly KnowledgeKind[] = KnowledgeKind.options;

/**
 * **How much has to be typed before anything is asked.**
 *
 * A one-character search over an org's whole world is not a search, it is the
 * org's whole world with a letter in it — and on the server it is also the one
 * shape the trigram index cannot serve (knowledge-base note §5, reported out of
 * chunk 1 as "wants a minimum length the UI half should own"). Below this the
 * page shows what it shows before it is asked anything: the most connected.
 */
export const KNOWLEDGE_MIN_QUERY = 2;

/** What the page's controls hold between them. */
export interface KnowledgeChoices {
  /** The search text, as it stands after the debounce. */
  q: string;
  /** The chosen kinds — none chosen means all three. */
  kinds: readonly KnowledgeKind[];
}

export const KNOWLEDGE_DEFAULTS: KnowledgeChoices = { q: '', kinds: [] };

/**
 * Whether what has been typed is a search yet. Below {@link KNOWLEDGE_MIN_QUERY}
 * it is not, and the page is the one it opened as.
 */
export function isKnowledgeSearch(q: string): boolean {
  return q.trim().length >= KNOWLEDGE_MIN_QUERY;
}

/**
 * The choices as parameters — which is also the query key, so the same shortest
 * form is what the cache holds and what the server receives (the rule
 * `people-query.ts` states).
 *
 * All three chips on is the same answer as none of them on, and it is sent as
 * none: a page that asked for `person&org&event` would be a second cache entry
 * for the list the page opens with.
 */
export function knowledgeParams(choices: KnowledgeChoices): KnowledgeSearchParams {
  const params: KnowledgeSearchParams = {};
  const q = choices.q.trim();
  if (isKnowledgeSearch(q)) params.q = q;
  const kinds = KNOWLEDGE_KINDS.filter((kind) => choices.kinds.includes(kind));
  if (kinds.length > 0 && kinds.length < KNOWLEDGE_KINDS.length) params.kind = kinds;
  return params;
}

/**
 * A chip pressed. Multi-select with no order of its own — the kinds come back in
 * the shared vocabulary's order, so pressing the same two in either order is one
 * read.
 */
export function toggleKind(kinds: readonly KnowledgeKind[], kind: KnowledgeKind): KnowledgeKind[] {
  const next = kinds.includes(kind) ? kinds.filter((chosen) => chosen !== kind) : [...kinds, kind];
  return KNOWLEDGE_KINDS.filter((candidate) => next.includes(candidate));
}

/** The kinds named on the address, ignoring anything that is not one of the three. */
export function readKinds(asked: readonly string[]): KnowledgeKind[] {
  return KNOWLEDGE_KINDS.filter((kind) => asked.includes(kind));
}

/**
 * **The address after the box settles.**
 *
 * `?q=` says what the page is searching for, so it is written only when the text
 * is a search — {@link isKnowledgeSearch}. A single typed letter used to reach
 * the address, where it claimed a search the page was not running and could not:
 * `knowledgeParams` drops anything under the floor, so `/knowledge?q=a` sent to
 * somebody else opened the page they would have seen with an empty box, with a
 * letter in it. Below the floor the parameter comes off; the box keeps what was
 * typed in its own state, so nothing disappears under the reader.
 *
 * A changed search also drops `?entity=`: the panel was the answer to the
 * question before this one, and leaving it open would show a reader something
 * their list no longer contains.
 */
export function addressForSearch(params: URLSearchParams, settled: string): URLSearchParams {
  const next = new URLSearchParams(params);
  if (isKnowledgeSearch(settled)) next.set('q', settled);
  else next.delete('q');
  next.delete('entity');
  return next;
}

/* ————————————————————————— the results ————————————————————————— */

/** One heading in the list, and the rows under it. */
export interface KindGroup {
  kind: KnowledgeKind;
  entries: KnowledgeEntry[];
}

/**
 * **The results, under the three headings the chips already name.**
 *
 * A flat list had to say what each row was, on every row — "Person · Tracked ·
 * Member · 31 connections · From board.xlsx" — and the kind was both the longest
 * word on the line and the one the reader could most easily have been told once.
 * Grouped, the heading says it for every row under it and the line says only
 * what differs.
 *
 * The kinds come out in the shared vocabulary's order, the same order the chips
 * and the opening view read; a kind nothing matched takes no heading at all,
 * because a search that found no events is not a page that should say "Events"
 * over a gap. Within a heading the server's order is kept — it is relevance, and
 * this is not the place to re-sort it.
 */
export function searchGroups(entries: readonly KnowledgeEntry[]): KindGroup[] {
  return KNOWLEDGE_KINDS.map((kind) => ({
    kind,
    entries: entries.filter((entry) => entry.kind === kind),
  })).filter((group) => group.entries.length > 0);
}

/**
 * **The one list the arrow keys walk**, flattened out of the headings.
 *
 * The rows are drawn in three runs but they are one list to a reader holding the
 * down arrow: stopping dead at the last person because the next row is under a
 * different heading would be the page's own layout getting in the way of reading
 * it. So the groups are rendered separately and moved through together.
 */
export function rowOrder(groups: readonly KindGroup[]): string[] {
  return groups.flatMap((group) => group.entries.map((entry) => entry.entity_id));
}

/* ————————————————————————— the panel's blocks ————————————————————————— */

/**
 * **Who it touches**, split the way §1.4 reads it: the people first — the server
 * has already put the tracked ones at the head of them — and then the
 * organisations and events on the same hop, in the order they arrived.
 *
 * Split here rather than rendered in one run because the two halves answer
 * different questions: who around this is already the office's, and what this
 * sits inside.
 */
export interface TouchGroups {
  people: KnowledgeEntry[];
  places: KnowledgeEntry[];
}

export function touchGroups(touches: readonly KnowledgeEntry[]): TouchGroups {
  return {
    people: touches.filter((entry) => entry.kind === 'person'),
    places: touches.filter((entry) => entry.kind !== 'person'),
  };
}

/** One heading of "What we know", and the facts under it. */
export interface FactGroup {
  type: FactType;
  facts: Fact[];
}

/**
 * The panel's facts, grouped by type in the catalog's own order.
 *
 * The catalog's order rather than the order the facts arrived in, so two
 * subjects of the same kind read down the same way; a type nothing on this
 * entity is filed under takes no heading at all.
 */
export function factGroups(facts: readonly Fact[]): FactGroup[] {
  return FACT_TYPES.map((type) => ({
    type,
    facts: facts.filter((fact) => fact.fact_type === type),
  })).filter((group) => group.facts.length > 0);
}

/**
 * **Where a provenance line points**, in the dashboard's own routing — the
 * reason the server sends an id and not a path (`shared/src/knowledge.ts`).
 *
 * A subject id *is* its entity id (`subject_record.entity_id` is the table's
 * primary key), which is what makes the last three of these reachable from a row
 * that carries nothing but the entity: a run and a piece of prose are both
 * subject-keyed, so both are about this person and both are read on their
 * record — the run on the research tab it is listed in, the note on the record
 * itself. An import is the one line that leaves the person entirely.
 *
 * **`on` is which record page is being read**, and it is the whole of what an
 * organisation changes here (organisations-as-subjects note §1 R8). An
 * organisation's record is one screen with no tabs, so all three of the
 * subject-keyed lines land on the same address: the run strip, the statements
 * and the graph are already on it. A path built for a person would send a reader
 * looking at an organisation to `/people/…`, which is a 404 with somebody else's
 * noun in it.
 *
 * **`'none'` is an entity with no record page at all**, and it is why this
 * returns a null (2026-09-10). The panel stands over every entity the org
 * holds, and most of them are not tracked: the office reading UT Austin — an
 * organisation nobody has tracked — was offered "a note" pointing at
 * `/organizations/:id`, which is a 404, because this builder asked what *kind*
 * the entity was and never whether it had a record. `/imports/:id` is the one
 * line that survives it, being a session page rather than the subject's.
 *
 * A **`link`** is null on every page for a different reason: it names a fact the
 * office assembled out of two picks, and there is no screen anywhere that shows
 * one prose row on its own. A **`split`** is null for the same reason: its id is
 * an `entity_split` decision, and a decision has no page — it is read in the log
 * on the record the reader already has open.
 */
export type ProvenanceOn = 'person' | 'org' | 'event' | 'none';

export function provenanceHref(
  entry: ProvenanceEntry,
  entityId: string,
  on: ProvenanceOn,
): string | null {
  switch (entry.kind) {
    case 'import':
      return `/imports/${entry.href_id}`;
    case 'link':
      return null;
    /* **A split has no page**, for `link`'s reason one line up: its `href_id` is
       an `entity_split` decision, and a decision has no screen of its own — the
       log is read on the record, which is the page the reader is already on.
       The line stands as text, which is all it was ever going to be. */
    case 'split':
      return null;
    case 'run':
      if (on === 'none') return null;
      if (on === 'org') return `/organizations/${entityId}`;
      if (on === 'event') return `/events/${entityId}`;
      return `/people/${entityId}/research`;
    case 'note':
    case 'intake':
      if (on === 'none') return null;
      if (on === 'org') return `/organizations/${entityId}`;
      if (on === 'event') return `/events/${entityId}`;
      return `/people/${entityId}`;
  }
}

/**
 * **Which record page an entity's own provenance lines belong on**, read off the
 * row the panel is already holding.
 *
 * `tracked` is the whole of it before the kind is: an entity the office does not
 * keep a record of has no record page, whatever kind it is. Only after that
 * does the kind choose the record page.
 */
export function provenanceOn(entry: {
  kind: KnowledgeKind;
  tracked: KnowledgeEntry['tracked'];
}): ProvenanceOn {
  if (entry.tracked === null) return 'none';
  if (entry.kind === 'org' || entry.kind === 'event') return entry.kind;
  return 'person';
}

/**
 * **Where a name points**, anywhere in the app that holds an entity id and
 * nothing else.
 *
 * Two destinations, and the split is the one `KnowledgePanelView` already makes
 * on its own head. A **tracked person** has a record of their own, and a subject
 * id *is* its entity id, so that record is reachable from the entity alone.
 * Everything else — an untracked person, and every organisation and event, which
 * the office does not follow and which has no dossier — has exactly one page
 * that says what the system knows about it: its knowledge-base entry, which is
 * the panel on `/knowledge` addressed by `?entity=` (`routes/knowledge.tsx`
 * keeps its selection on the address for this reason).
 *
 * The kind is the pair's, not the side's, because that is where the wire carries
 * it: both sides of a merge question are the same kind by construction.
 */
export function entityHref(
  side: { entity_id: string; tracked: boolean },
  kind: KnowledgeKind,
): string {
  if (!side.tracked) return `/knowledge?entity=${encodeURIComponent(side.entity_id)}`;
  /* **A followed organisation has a record of its own too** (2026-09-10). It has
     had one since organisations became subjects, and this builder still sent it
     to the knowledge base — not a 404, but the wrong page: the reader answering
     "are these the same?" wants the record's own Overview and Network, which is
     precisely what the panel does not show. A tracked event has its page too;
     an untracked one takes the panel. */
  if (kind === 'person') return `/people/${side.entity_id}`;
  if (kind === 'org') return `/organizations/${side.entity_id}`;
  if (kind === 'event') return `/events/${side.entity_id}`;
  return `/knowledge?entity=${encodeURIComponent(side.entity_id)}`;
}

/**
 * **Where a Needs Action heading and its rows point** — the subject's own record
 * page, whichever kind of record that is (2026-09-10).
 *
 * Home built this path by hand as `/people/:id/research` for every entry, which
 * was true for as long as the only thing the office followed was a person. An
 * organisation's failed lookup then put a row on Home whose link was a **404**
 * with somebody else's noun in it — the failure the office reported. A uuid does
 * not say what it names, so the kind comes down the wire on the entry
 * (`shared/src/needs-action.ts`) and is dispatched here.
 *
 * A person lands on **Research**, because every row this builds a link for is
 * about a run or a finding and that is the tab they live on. An organisation
 * lands on its record, whose Overview carries the run strip and whose tabs are
 * two — there is no research tab to send anybody to.
 */
export function subjectHref(subject: { id: string; kind: 'person' | 'org' | 'event' }): string {
  if (subject.kind === 'org') return `/organizations/${subject.id}`;
  if (subject.kind === 'event') return `/events/${subject.id}`;
  return `/people/${subject.id}/research`;
}

/**
 * **Where a rename proposal's record link points** (organisations-as-subjects
 * note §2 chunk 3a).
 *
 * `entityHref` above cannot answer this: it asks whether the side is *tracked*,
 * and `NeedsActionRenameProposal` carries a kind and no such flag — deliberately,
 * because the row is a question about a name and not a reading of a record.
 *
 * An **organisation** goes to its own record page. Every proposal a lookup files
 * is one, by construction: the server refuses to create the decision when the
 * run's subject is not an organisation, and the office's own renames are filed
 * already answered and never reach this list. The other two kinds are here
 * because the decision kind is kind-agnostic by ruling (R6) and go to the
 * knowledge base, which is the one page that exists for an entity of any kind —
 * for a person it is also the honest answer, there being nothing on the row that
 * says whether they have a record of their own.
 */
export function renameProposalHref(proposal: {
  entity_id: string;
  entity_kind: 'person' | 'org' | 'event';
}): string {
  return proposal.entity_kind === 'org'
    ? `/organizations/${proposal.entity_id}`
    : `/knowledge?entity=${encodeURIComponent(proposal.entity_id)}`;
}

/* ————————————————————————— the keyboard ————————————————————————— */

/** The keys the list answers to, and nothing else. */
export type ListKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

export const LIST_KEYS: readonly string[] = ['ArrowDown', 'ArrowUp', 'Home', 'End'];

/**
 * **Where an arrow key lands.**
 *
 * The rows are a list of ids and the caret is one of them, so moving is
 * arithmetic and is asserted as arithmetic. It **does not wrap**: a list of
 * search results has a top and a bottom, and a reader holding the down arrow
 * expects to stop at the last row rather than to be returned silently to the
 * first. Nothing selected and a down arrow is the first row, which is how a
 * reader gets into the list from the search box.
 *
 * Returns null when there is nowhere to go — an empty list, or an id that is not
 * in it — and the caller then leaves focus where it is.
 */
export function moveInList(
  ids: readonly string[],
  current: string | null,
  key: ListKey,
): string | null {
  if (ids.length === 0) return null;
  const first = ids[0] as string;
  const last = ids[ids.length - 1] as string;
  if (key === 'Home') return first;
  if (key === 'End') return last;

  const at = current === null ? -1 : ids.indexOf(current);
  if (at === -1) return key === 'ArrowDown' ? first : last;
  const next = key === 'ArrowDown' ? at + 1 : at - 1;
  if (next < 0 || next >= ids.length) return null;
  return ids[next] as string;
}

/* ————————————————————— the People screen's footnote ————————————————————— */

/**
 * **How many known people a People search is not showing** (§1.6).
 *
 * People is tracked-only now, and the same word typed into it may well name
 * somebody the org holds without tracking. The count comes from the knowledge
 * base's own search rather than from a second server route: a known person is
 * an answer with no record behind it, which on this contract is `tracked: null`
 * and nothing else.
 *
 * It is a **floor**. The search caps at fifty and the cap counts tracked and
 * known together, so a capped answer is one this can only say "at least" about —
 * which is what {@link KnowledgeSearchResponse.capped} is carried here for.
 */
export function alsoKnownCount(response: KnowledgeSearchResponse | undefined): number {
  if (!response) return 0;
  return response.entries.filter((entry) => entry.tracked === null).length;
}
